/**
 * CampaignRankingContentItem
 *
 * One row in the content (most-likes / most-coins) ranking leaderboard.
 *
 * Mirrors Flutter's `CampainRankingContentItem`
 * (lib/presentation/screens/campaigns/widgets/campain_ranking_content_item.dart):
 * - `:41-46` — leading circular avatar from `generateUserProfileImageUrl(firebaseId)`.
 * - `:56-67` — winner icon (`assets/images/campaign.png`, tinted `exclusive`),
 *   shown only when `winner`.
 * - `:68-78` — name shows "You" for the current user, else `userName`.
 * - `:81-90` — subtitle `"{count} likes"`.
 * - `:93-118` — trailing square content thumbnail, tap → `onMediaTap(content)`;
 *   whole row tap → `onItemTap(firebaseId)`.
 *
 * The thumbnail CDN URL is built `${env.CDN_NAME}${content.relativeThumbnailPath}`
 * (Flutter's `thumbnail` getter prepends `Env.cdnName`; mirrors mediaUtils.ts:42).
 * Web tokens (theme / responsive / l10n) follow web conventions; the avatar
 * reuses `CachedImage` + `UserUtils`, the winner icon reuses `AssetIcon`, and
 * the trailing thumbnail reuses the common `ContentThumbnail`.
 */
import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { ContentThumbnail } from '@/presentation/feature/content/components/ContentThumbnail';
import { UserUtils } from '@/domain/utils/userUtils';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { env } from '@/config/env';
import type { CampaignRankingContentEntity } from '@/domain/entities/campaign/campaignRankingContentEntity';

export interface CampaignRankingContentItemProps {
  content: CampaignRankingContentEntity;
  isCurrentUser: boolean;
  onItemTap: (firebaseId: string) => void;
  onMediaTap: (content: CampaignRankingContentEntity) => void;
}

export const CampaignRankingContentItem: React.FC<CampaignRankingContentItemProps> = ({
  content,
  isCurrentUser,
  onItemTap,
  onMediaTap,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const displayName = isCurrentUser ? l10n.campaigns.you : content.userName;
  const likesText = l10n.campaigns.likesCount.replace('{count}', String(content.likes));
  const thumbnailUrl = `${env.CDN_NAME}${content.relativeThumbnailPath}`;

  const handleMediaTap = (e: React.MouseEvent) => {
    // Stop the row's onItemTap from also firing when the thumbnail is tapped.
    e.stopPropagation();
    onMediaTap(content);
  };

  return (
    <div
      {...qaAttr({ id: `campaign-rank-content-${content.firebaseId}` })}
      onClick={() => onItemTap(content.firebaseId)}
      style={{
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
      }}
    >
      <CachedImage
        src={UserUtils.generateUserProfileImageUrl(content.firebaseId)}
        alt={displayName}
        style={{
          width: responsive.avatar.large,
          height: responsive.avatar.large,
          borderRadius: responsive.borderRadius.round,
          objectFit: 'cover',
          flexShrink: 0,
        }}
      />

      <div style={{ flex: 1, margin: `0 ${responsive.spacing.base}px`, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: responsive.spacing.xs }}>
          {content.winner && (
            <AssetIcon
              path="/assets/images/campaign.png"
              color={theme.exclusive}
              width={responsive.icon.small}
              height={responsive.icon.small}
            />
          )}
          <span
            style={{
              fontWeight: responsive.fontWeight.semibold,
              color: theme.text.primary,
              fontSize: responsive.font.base,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayName}
          </span>
        </div>
        <span
          style={{
            display: 'block',
            marginTop: responsive.spacing.xs,
            fontSize: responsive.font.sm,
            color: theme.text.secondary,
          }}
        >
          {likesText}
        </span>
      </div>

      <div
        onClick={handleMediaTap}
        style={{
          width: responsive.avatar.large,
          height: responsive.avatar.large,
          flexShrink: 0,
          borderRadius: responsive.borderRadius.base,
          overflow: 'hidden',
          cursor: 'pointer',
        }}
      >
        <ContentThumbnail
          thumbnailUrl={thumbnailUrl}
          alt={displayName}
          qa={{ id: `campaign-rank-content-thumbnail-${content.firebaseId}` }}
        />
      </div>
    </div>
  );
};
